// import dynamic from "next/dynamic";
// const { JSEncrypt } = dynamic(() => import("jsencrypt"), {
//   ssr: false,
// });
import sessionStorage from "utils/sessionStorage";

/**
 * 根据当前顶级域名，获取二级域名链接
 * @param api，对应二级域名字符串，为空时返回顶级域名
 * @param noProtocol，获取链接默认是带有协议的，如果该字段存在，不包含协议
 *     如果 noProtocol === "ws" 会更加当前http协议添加 ws:// 或 wss://
 */
export function getHosts() {
  let host, protocol;
  if (typeof window === "undefined") {
    //server side
    host = process.env.NEXT_PUBLIC_host;
    protocol = process.env.NEXT_PUBLIC_protocol;
  } else {
    const isLocal =
      location.hostname === "localhost" ||
      /^((25[0-5]|(2[0-4]|1\d|[1-9]|)\d)(\.(?!$)|$)){4}$/.test(
        location.hostname
      );
    host = isLocal ? process.env.NEXT_PUBLIC_host : location.host;
    protocol = isLocal ? process.env.NEXT_PUBLIC_protocol : location.protocol;
  }

  return { host, protocol };
}
export function getOrigin(api, noProtocolOrWs) {
  const { host, protocol } = getHosts();

  const topDomain = host.split(".").slice(-2).join(".");
  const domain = (api ? `${api}.` : "") + topDomain;
  if (!noProtocolOrWs) return `${protocol}//` + domain;
  if (noProtocolOrWs === "ws")
    return `${protocol.includes("https") ? "wss" : "ws"}://` + domain;
  return domain;
}

/**
 * 正则表达式校验
 * @param type，验证类型，譬如：email 邮箱
 * @param content，需要验证的字符串
 * @param retRegExp，可选，如果存在，返回正则表达式
 * @returns，Boolean 是否匹配，或是是正则表达式
 */
export function regexp(type, content, retRegExp) {
  const verify = {
    email: /^([a-zA-Z0-9_\.-])+@([a-z0-9A-Z]+(-[a-z0-9A-Z]+)?\.)+[a-zA-Z]{2,}$/,
    phone: /^[0-9]{5,11}$/,
    loginPassword: /^(?=.*[0-9].*)(?=.*[A-Z].*).{8,1000}$/,
    securityPassword:
      /^(?![\d]+$)(?![a-zA-Z]+$)(?![!@#\$%\^&\*\(\)]+$)[\da-zA-Z!@#\$%\^&\*\(\)]{6,20}$/,
    securityPassword2:
      /^(?![\d]+$)(?![a-zA-Z]+$)(?![!@#\$%\^&\*\(\)]+$)[\da-zA-Z!@#\$%\^&\*\(\)]{2,20}$/,
  };

  if (!verify[type]) return;

  if (retRegExp) return verify[type];

  return verify[type].test(content);
}

/**
 * 加密字符串
 * @param key，getPublicKey获得的密钥
 * @param value，被加密字符串
 * @returns {string}，加密后的文本
 */
export function doRsaEncrypt(key, value) {
  if (typeof window === "undefined") return;
  if (!key || !value) return value;
  // console.info("doRsaEncrypt===============");
  const { JSEncrypt } = require("jsencrypt");
  // console.info("JSEncrypt =", JSEncrypt);
  const encrypt = new JSEncrypt();
  encrypt.setPublicKey(key);
  const encrypted = encrypt.encrypt(value);
  return encrypted;
}

/**
 * 将 text 文本复制到剪切板
 * @param text，需要复制的文本
 * 这个是同步函数，返回 true 说明复制成功，false 失败
 */
export function copy(text) {
  if (typeof window === "undefined") return;
  const ele = document.createElement("textarea");
  ele.readonly = "readonly";
  ele.style.position = "fixed";
  ele.style.opacity = "0";
  ele.style.width = "0";
  ele.style.height = "0";
  ele.value = text;
  document.body.appendChild(ele);
  ele.focus();
  //ele.select(); //not working in mobile
  ele.setSelectionRange(0, ele.value.length);
  const result = document.execCommand("copy");
  ele.parentNode.removeChild(ele);
  return result;
}

/**
 * 获取带域名的绝对路径
 * @param {string} pathname, 路径地址，譬如 /login，
 * @param {string} locale, 语言路径，可选，譬如 zh-CN
 * @param {object} query, 链接参数，可选，譬如 {a: 1, b:2}
 * @param {string} basePath, 链接前缀，可选，默认 accounts
 */
export function getAbsoluteUrl({
  pathname,
  locale,
  query,
  basePath = "accounts",
}) {
  const { host, protocol } = getHosts();
  let domain = protocol + "//" + host;
  /*
  let domain =
    process.env.NEXT_PUBLIC_protocol + "//" + process.env.NEXT_PUBLIC_host;
   */
  if (
    process.env.DEBUG ||
    (typeof location !== "undefined" &&
      (location.hostname === "localhost" ||
        /^((25[0-5]|(2[0-4]|1\d|[1-9]|)\d)(\.(?!$)|$)){4}$/.test(
          location.hostname
        )))
  ) {
    domain = "http://localhost:8081";
  }

  let url =
    domain +
    (locale ? `/${locale}` : "") +
    (basePath ? `/${basePath}` : "") +
    pathname;

  if (query) {
    let search = "";
    for (let va in query) {
      search += (search ? "&" : "") + va + "=" + encodeURIComponent(query[va]);
    }
    if (search) {
      if (url.includes("?")) {
        if (url[url.length - 1] === "?") {
          url += search;
        } else {
          url += "&" + search;
        }
      } else {
        url += "?" + search;
      }
    }
  }

  return url;
}

/**
 * 重定向，对满足条件的链接进行重定向
 * @router
 */
export function redirectBackUrl(
  router,
  {
    noElse = false, //如果 backurl 不符合条件，就没有下文了，否则重定向到默认地址
    method = "replace", //重定向方法
    defaultArgs = undefined, //默认重定向内容
  } = {}
) {
  if (sessionStorage.get("oauth2")) {
    return router.replace({
      pathname: "/accounts/oauth2",
      query: router.query,
    });
  }

  const { backurl } = router.query;

  if (backurl) {
    const topDomain = getOrigin("", "none");
    const regExp = new RegExp(
      "^(http://|https://)(\\w+\\.)?" + topDomain + "($|/.*|\\?.*)",
      "i"
    );
    const isLegal = regExp.test(backurl); //是否为合法回调地址
    if (isLegal) {
      if (typeof location !== "undefined") {
        if (method === "replace") {
          location.replace(backurl);
        } else {
          location.href = backurl;
        }
      } else {
        router[method](backurl);
      }

      return;
    }
  }

  if (noElse) return;

  let url = defaultArgs || getOrigin("www");
  if (typeof location !== "undefined") {
    if (typeof url === "object")
      url = getAbsoluteUrl({ locale: router.locale, ...url });
    if (method === "replace") {
      location.replace(url);
    } else {
      location.href = url;
    }
  } else {
    router[method](url);
  }
}

export function getExplore() {
  var Sys = {};
  var ua = navigator.userAgent.toLowerCase();
  var s;
  (s = ua.match(/rv:([\d.]+)\) like gecko/))
    ? (Sys.ie = s[1])
    : (s = ua.match(/msie ([\d\.]+)/))
    ? (Sys.ie = s[1])
    : (s = ua.match(/edge\/([\d\.]+)/))
    ? (Sys.edge = s[1])
    : (s = ua.match(/firefox\/([\d\.]+)/))
    ? (Sys.firefox = s[1])
    : (s = ua.match(/(?:opera|opr).([\d\.]+)/))
    ? (Sys.opera = s[1])
    : (s = ua.match(/chrome\/([\d\.]+)/))
    ? (Sys.chrome = s[1])
    : (s = ua.match(/version\/([\d\.]+).*safari/))
    ? (Sys.safari = s[1])
    : 0;
  // 根据关系进行判断
  if (Sys.ie) return "IE V" + Sys.ie;
  if (Sys.edge) return "EDGE V" + Sys.edge;
  if (Sys.firefox) return "Firefox V" + Sys.firefox;
  if (Sys.chrome) return "Chrome V" + Sys.chrome;
  if (Sys.opera) return "Opera V" + Sys.opera;
  if (Sys.safari) return "Safari V" + Sys.safari;
  return "Unknown";
}

export function browserRedirect() {
  var sUserAgent = navigator.userAgent;
  var isWin = navigator.platform == "Win32" || navigator.platform == "Windows";
  var isMac =
    navigator.platform == "Mac68K" ||
    navigator.platform == "MacPPC" ||
    navigator.platform == "Macintosh" ||
    navigator.platform == "MacIntel";
  if (isMac) return "Mac OS";
  var isUnix = navigator.platform == "X11" && !isWin && !isMac;
  if (isUnix) return "Unix";
  var isLinux = String(navigator.platform).indexOf("Linux") > -1;
  if (isLinux) return "Linux";
  if (isWin) {
    var isWin2K =
      sUserAgent.indexOf("Windows NT 5.0") > -1 ||
      sUserAgent.indexOf("Windows 2000") > -1;
    if (isWin2K) return "Win2000";
    var isWinXP =
      sUserAgent.indexOf("Windows NT 5.1") > -1 ||
      sUserAgent.indexOf("Windows XP") > -1;
    if (isWinXP) return "WinXP";
    var isWin2003 =
      sUserAgent.indexOf("Windows NT 5.2") > -1 ||
      sUserAgent.indexOf("Windows 2003") > -1;
    if (isWin2003) return "Win2003";
    var isWinVista =
      sUserAgent.indexOf("Windows NT 6.0") > -1 ||
      sUserAgent.indexOf("Windows Vista") > -1;
    if (isWinVista) return "WinVista";
    var isWin7 =
      sUserAgent.indexOf("Windows NT 6.1") > -1 ||
      sUserAgent.indexOf("Windows 7") > -1;
    if (isWin7) return "Win7";
    var isWin10 =
      sUserAgent.indexOf("Windows NT 10") > -1 ||
      sUserAgent.indexOf("Windows 10") > -1;
    if (isWin10) return "Win10";
  }
  return "Unknown";
}

export function getUUID() {
  let d = new Date().getTime();
  let uuid = "xxxxxxxx-xxxx-xxxx-yxxx-xxxxxxxxxxxx".replace(
    /[xy]/g,
    function (c) {
      let r = (d + Math.random() * 16) % 16 | 0;
      d = Math.floor(d / 16);
      return (c == "x" ? r : (r & 0x3) | 0x8).toString(16);
    }
  );
  return uuid;
}

/**
 * 获取url中search字段，譬如 getUrlSearchAttr('token')
 * @param regStr，属性字段
 * @param exact，是否精确匹配大小写
 * @param urlStr，如果存在，使用该字符串作为查找对象
 * @returns {string}，返回对应属性的值
 */
export function getUrlSearchAttr(regStr, urlStr, exact) {
  if (!urlStr) {
    if (typeof window === "undefined") return;
    urlStr = window.location.href;
  }
  urlStr.indexOf("?") < 0 && (urlStr = "?" + urlStr);
  let todoStr = urlStr;

  const regExp = new RegExp("[?&]" + regStr + "=[^&]+", exact ? "g" : "gi");
  let value = "",
    temp;
  todoStr.replace(regExp, (str) => {
    temp = str.split("=")[1];
    temp && (value = decodeURIComponent(temp));
  });

  return value;
}
